import { OrbitControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useEffect, useState } from "react";
import * as THREE from 'three';

type ModelViewerProps = {
    model: string | File;
};

export const ModelViewer = ({ model }: ModelViewerProps) => {
    const [loadedModel, setLoadedModel] = useState<THREE.Group<THREE.Object3DEventMap> | THREE.Object3D>();

    useEffect(() => {
        if (model) {
            let ignore = false;

            if (typeof model === 'string') { // URL
                const extension = model.split('.').pop()?.toLowerCase();
                
                switch (extension) {
                    case '3dm': {
                        const loadModel = async () => {
                            const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');
                            const loader = new Rhino3dmLoader();
                            loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);
    
                            loader.load(model, (object) => {
                                if (ignore) return;
                                setLoadedModel(object);
                            });
                        };
                        loadModel();
                        break;
                    }
                    case 'glb':
                    case 'gltf': {
                        const loadModel = async () => {
                            const manager = new THREE.LoadingManager();
    
                            const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                            const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                            const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                            const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');
    
                            const dracoLoader = new DRACOLoader();
                            dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);
    
                            const ktx2Loader = new KTX2Loader(manager);
                            ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);
    
                            const loader = new GLTFLoader(manager);
                            loader.setDRACOLoader(dracoLoader);
                            loader.setKTX2Loader(ktx2Loader);
                            loader.setMeshoptDecoder(MeshoptDecoder);
    
                            loader.load(model, (gltf) => {
                                if (ignore) return;
                                setLoadedModel(gltf.scene);
                            });
                        };
                        loadModel();
    
                        break;
                    }
                    case 'obj': {
                        const loadModel = async () => {
                            const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');
    
                            const loader = new OBJLoader();
                            
                            loader.load(model, (object) => {
                                if (ignore) return;
                                setLoadedModel(object);
                            });
                        };
                        loadModel();
                        break;
                    }
                    case 'drc': {
                        const loadModel = async () => {
                            const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
    
                            const loader = new DRACOLoader();
                            loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);
    
                            loader.load(model, (geometry) => {
                                if (ignore) return;
    
                                let object;
    
                                if ( geometry.index !== null ) {
    
                                    const material = new THREE.MeshStandardMaterial();
    
                                    object = new THREE.Mesh(geometry, material);
    
                                } else {
    
                                    const material = new THREE.PointsMaterial({ size: 0.01 });
                                    material.vertexColors = geometry.hasAttribute('color');
    
                                    object = new THREE.Points(geometry, material);
    
                                }
    
                                setLoadedModel(object);
                            });
                        };
                        loadModel();
                        
                        break;
                    }
                    case 'fbx': {
                        const loadModel = async () => {
                            const manager = new THREE.LoadingManager();
                            const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');
    
                            const loader = new FBXLoader(manager);
    
                            loader.load(model, (object) => {
                                if (ignore) return;
    
                                object.traverse(object => {
                                    const _object: any = object;
                                    if (_object.material) {
                                        _object.material = new THREE.MeshStandardMaterial({
                                            color: _object.material.color
                                        });
                                    }
                                });
                                
                                setLoadedModel(object);
                            });
                        };
                        loadModel();
    
                        break;
                    }
                    //case 'max':
                    case '3ds': {
                        const loadModel = async () => {
                            const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                            const loader = new TDSLoader();
    
                            loader.load(model, (object) => {
                                if (ignore) return;
                                setLoadedModel(object);
                            });
                        };
                        loadModel();
    
                        break;
                    }
                }
    
            } else { // File
                const extension = model.name.split('.').pop()?.toLowerCase();
                
                switch (extension) {
                    case '3dm': {
                        const fileReader = new FileReader();
    
                        fileReader.addEventListener('load', async (event) => {
                            if (ignore) return;
    
                            const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');
    
                            const loader = new Rhino3dmLoader();
                            loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);
    
                            loader.parse(event.target?.result as ArrayBufferLike , (object) => {
                                if (ignore) return;
                                setLoadedModel(object);
                            });
                        }, false);
                        fileReader.readAsArrayBuffer(model);
    
                        break;
                    }
                    case 'glb':
                    case 'gltf': {
                        const fileReader = new FileReader();
    
                        fileReader.addEventListener('load', async (event) => {
                            if (ignore) return;
    
                            const manager = new THREE.LoadingManager();
    
                            const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                            const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                            const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                            const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');
    
                            const dracoLoader = new DRACOLoader();
                            dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);
    
                            const ktx2Loader = new KTX2Loader(manager);
                            ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);
    
                            const loader = new GLTFLoader(manager);
                            loader.setDRACOLoader(dracoLoader);
                            loader.setKTX2Loader(ktx2Loader);
                            loader.setMeshoptDecoder(MeshoptDecoder);
    
                            loader.parse(event.target?.result!, '', (gltf) => {
                                if (ignore) return;
    
                                loader.dracoLoader?.dispose();
                                loader.ktx2Loader?.dispose();

                                setLoadedModel(gltf.scene);
                            });
                        });
                        fileReader.readAsArrayBuffer(model);
    
                        break;
                    }
                    case 'obj': {
                        const fileReader = new FileReader();
    
                        fileReader.addEventListener('load', async (event) => {
                            if (ignore) return;
    
                            const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');
                            const loader = new OBJLoader();
    
                            const object = loader.parse(event.target?.result as string);
    
                            if (ignore) return;
    
                            setLoadedModel(object);
                        }, false);
                        fileReader.readAsText(model);
    
                        break;
                    }
                    case 'drc': {
                        const fileReader = new FileReader();
    
                        fileReader.addEventListener('load', async (event) => {
                            if (ignore) return;
    
                            const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
    
                            const loader = new DRACOLoader();
                            loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);
    
                            loader.parse(event.target?.result as ArrayBuffer, (geometry) => {
                                if (ignore) return;
    
                                let object;
    
                                if ( geometry.index !== null ) {
    
                                    const material = new THREE.MeshStandardMaterial();
    
                                    object = new THREE.Mesh(geometry, material);
    
                                } else {
    
                                    const material = new THREE.PointsMaterial({ size: 0.01 });
                                    material.vertexColors = geometry.hasAttribute('color');
    
                                    object = new THREE.Points(geometry, material);
    
                                }
    
                                setLoadedModel(object);
                            });
                        }, false);
                        fileReader.readAsArrayBuffer(model);
                        
                        break;
                    }
                    case 'fbx': {
                        const fileReader = new FileReader();
    
                        fileReader.addEventListener('load', async (event) => {
                            if (ignore) return;
    
                            const manager = new THREE.LoadingManager();
                            const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');
    
                            const loader = new FBXLoader(manager);
    
                            const object = loader.parse(event.target?.result!, '');
    
                            object.traverse(object => {
                                const _object: any = object;
                                if (_object.material) {
                                    _object.material = new THREE.MeshStandardMaterial({
                                        color: _object.material.color
                                    });
                                }
                            });
    
                            if (ignore) return;
                            setLoadedModel(object);
                        });
                        fileReader.readAsArrayBuffer(model);
    
                        break;
                    }
                    case '3ds': {
                        const fileReader = new FileReader();
    
                        fileReader.addEventListener('load', async (event) => {
                            if (ignore) return;
    
                            const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                            const loader = new TDSLoader();
    
                            const object = loader.parse(event.target?.result as ArrayBuffer, '');
    
                            if (ignore) return;
                            setLoadedModel(object);
                        });
                        fileReader.readAsArrayBuffer(model);
    
                        break;
                    }
                }
            }
            
            return () => {
                ignore = true;
            };
        }
    }, [model]);

    return (
        <Canvas style={{ maxWidth: '60px', maxHeight: '60px' }} camera={{ position: [2, 2, 8] }}>
            <OrbitControls makeDefault />
            <ambientLight />
            <group>
                { loadedModel && <primitive object={loadedModel} /> }
            </group>
        </Canvas>
    );
};